import { useMemo } from "react";
import { motion } from "framer-motion";
import { SmartImage } from "./SmartImage";

const items = [
  {
    src: "/assets/curb-speech.png",
    caption: "\u201CSolana is dead\u201D — the room, 2022",
    rot: "-1deg",
  },
  { src: "/assets/gallery/curb-porch-golden.jpg", caption: "the trenches, at golden hour", rot: "1deg" },
  { src: "/assets/gallery/curb-vs-bear.jpg", caption: "bear market? name a better crime scene.", rot: "-1.2deg" },
  { src: "/assets/bull-cigar.png", caption: "victory cigar. $CURB galaxy.", rot: "1.5deg" },
  { src: "/assets/gallery/curb-herd-leader.jpg", caption: "the herd doesn't move without him.", rot: "-1deg" },
  { src: "/assets/gallery/curb-airdrop.jpg", caption: "airdrop szn. curb rides in.", rot: "1.2deg" },
  { src: "/assets/curb-gym.png", caption: "built different. literally.", rot: "-1.5deg" },
  { src: "/assets/gallery/curb-revolution.jpg", caption: "vive la $curb révolution.", rot: "1deg" },
  { src: "/assets/gallery/curb-cosmic.jpg", caption: "$curb rides the cosmic vacuum.", rot: "-1deg" },
  { src: "/assets/gallery/curb-locomotive.jpg", caption: "the $curb line runs on time.", rot: "1.3deg" },
  { src: "/assets/trusted-trinity.png", caption: "$CURB × $ANSEM — the trusted trinity", rot: "1.2deg" },
  { src: "/assets/gallery/curb-scripture.jpg", caption: "the trenches read scripture at dusk.", rot: "-1.3deg" },
  { src: "/assets/gallery/curb-stage.jpg", caption: "you laugh now. see you at ATH.", rot: "1deg" },
  { src: "/assets/gallery/curb-mirror.jpg", caption: "mirror check: still bullish.", rot: "-1deg" },
  { src: "/assets/gallery/curb-sunset-patrol.jpg", caption: "solana sunset patrol.", rot: "1.2deg" },
  { src: "/assets/gallery/curb-constellation.jpg", caption: "$curb was written in the stars.", rot: "-1deg" },
  { src: "/assets/gallery/curb-cult.jpg", caption: "$curb cult · solana szn.", rot: "1.4deg" },
  { src: "/assets/bull-super.png", caption: "SOLANA BULL MODE: engaged", rot: "-1deg" },
  // ── New drop (Feb 2026) ──────────────────────────────────────────────
  { src: "/assets/gallery/curb-neon-skyline.jpg", caption: "the herd faces the skyline.", rot: "1deg" },
  { src: "/assets/gallery/curb-portrait-charts.jpg", caption: "portrait mode. still bullish.", rot: "-1.3deg" },
  { src: "/assets/gallery/curb-storm-wings.jpg", caption: "強気の相場は、ソラナが牽引する.", rot: "1deg" },
  { src: "/assets/gallery/curb-diamond-drip.jpg", caption: "the drip is on-chain.", rot: "-1deg" },
  { src: "/assets/gallery/curb-chad-ride.jpg", caption: "riding the bull. no seatbelt.", rot: "1.4deg" },
  { src: "/assets/gallery/curb-anime-chad.jpg", caption: "$curb — final form.", rot: "-1.2deg" },
  { src: "/assets/gallery/curb-green-showdown.jpg", caption: "final showdown at the wick.", rot: "1deg" },
];

// Fisher-Yates shuffle — returns a new array, doesn't mutate the original.
const shuffled = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const Gallery = () => {
  // Shuffle order once per mount so the gallery feels fresh on every visit,
  // while staying stable during scroll (useMemo prevents reshuffle on re-render).
  const shuffledItems = useMemo(() => shuffled(items), []);
  return (
  <section id="gallery" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="mb-12 flex items-end justify-between flex-wrap gap-4"
    >
      <div>
        <p className="font-bebas tracking-[0.35em] text-[#9945FF] text-lg">THE EVIDENCE LOCKER</p>
        <h2 data-testid="gallery-heading" className="font-marker text-4xl sm:text-5xl gradient-sol text-glow-green mt-2 -rotate-1 inline-block">
          CURB&apos;S CAMERA ROLL
        </h2>
      </div>
      <p className="font-marker text-zinc-500 rotate-1">no context will be provided.</p>
    </motion.div>

    <div className="columns-2 sm:columns-2 md:columns-3 gap-2 sm:gap-5">
      {shuffledItems.map((it, i) => (
        <motion.figure
          key={it.src}
          data-testid={`gallery-item-${i + 1}`}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, delay: (i % 3) * 0.12 }}
          className="sticker relative overflow-hidden rounded-xl sm:rounded-2xl border border-[#9945FF]/30 group break-inside-avoid mb-2 sm:mb-5"
          style={{ "--stick-rot": it.rot }}
        >
          <SmartImage
            src={it.src}
            alt={it.caption}
            className="w-full h-auto block group-hover:scale-105 transition-transform duration-500"
          />
          <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#050409]/95 to-transparent px-2.5 sm:px-4 pt-8 sm:pt-10 pb-2 sm:pb-3">
            <span className="font-marker text-[#14F195] text-[11px] leading-tight sm:text-sm md:text-base">{it.caption}</span>
          </figcaption>
        </motion.figure>
      ))}
    </div>
  </section>
  );
};
